import React, { useEffect, useRef, useState } from 'react';

import { Map } from 'immutable';
import qs from 'qs';
import { Resource } from 'registration/types';

import { Checkbox } from 'shared/components/ihcl/checkbox';
import { Input } from 'shared/components/ihcl/input';
import { styled } from 'shared/components/ihcl/styled';
import { Button } from 'shared/components/ihcl/v2/button';
import sharedConstants from 'shared/constants/constants.json';
import { genExternalLinkHandler } from 'shared/helpers/linking';
import { authenticityToken } from 'shared/helpers/rails';
import { trackClick } from 'shared/helpers/tracking';
import { validateRequired } from 'shared/helpers/validations';

import getGoogleClickID from '../../helpers/getGoogleClickID';

export const AltPrompt = styled('div', {
  marginTop: '36px',
  marginBottom: '36px',
});

export const InputWrapper = styled('div', {
  marginBottom: '20px',
  textAlign: 'left',
});

export const SignInOptionsWrapper = styled('div', ({ $theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '20px',
  ...$theme.typography.DisplaySmall,
}));

const FooterWrapper = styled('div', {
  fontSize: '14px',
});

const Subtitle = styled('p', {
  marginTop: '20px',
  marginBottom: '30px',
  fontSize: '16px',
});

export function parseInputObj(name, value, nameValuePairs = []) {
  let parsedNameValuePairs = [...nameValuePairs];
  if (value && typeof value === 'object') {
    if (Array.isArray(value)) {
      value.forEach((subValue) => {
        parsedNameValuePairs = parsedNameValuePairs.concat(
          parseInputObj(`${name}[]`, subValue, nameValuePairs)
        );
      });
    } else {
      Object.keys(value).forEach((valueKey) => {
        parsedNameValuePairs = parsedNameValuePairs.concat(
          parseInputObj(`${name}[${valueKey}]`, value[valueKey], nameValuePairs)
        );
      });
    }
  } else {
    parsedNameValuePairs.push([name, value]);
  }
  return parsedNameValuePairs;
}

function hiddenInputIfValue(name, value) {
  if (value && typeof value === 'object') {
    const nameValuePairs = parseInputObj(name, value);
    return nameValuePairs.map(([inputName, inputValue]) =>
      inputValue ? (
        <input
          name={inputName}
          value={inputValue}
          type="hidden"
          key={`${inputName}:${inputValue}`}
        />
      ) : null
    );
  }
  return value ? (
    <input name={name} value={value} type="hidden" key={name} />
  ) : null;
}

type SignXFormProps = {
  initialErrors: {
    email?: boolean | string;
  };
  intent: string;
  isSignUp?: boolean;
  previousData: Partial<Resource>;
  profileAttributes?: any;
};

function SignXForm({
  initialErrors,
  intent,
  isSignUp = false,
  previousData,
  profileAttributes = {},
}: SignXFormProps) {
  const signXForm = useRef<HTMLFormElement>(null);

  const emailFromPreviousData = previousData?.email || '';
  const { email: emailError } = initialErrors || {};

  const [email, setEmail] = useState(emailFromPreviousData);
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [errors, setErrors] = useState<Map<string, string | boolean>>(
    Map(emailError ? { email: emailError[0] } : {})
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    mixpanel.track('Onboard Lite Step 2');
  }, []);

  const validateAndSubmit = () => {
    let validationErrors = validateRequired(
      { email, password },
      'email',
      'password'
    );

    if (isSignUp) {
      if (!validationErrors.has('email') && email.indexOf('@') === -1) {
        validationErrors = validationErrors.set(
          'email',
          'Seems like there might be a problem here.'
        );
      }

      if (!validationErrors.has('password') && password.trim().length < 6) {
        validationErrors = validationErrors.set(
          'password',
          'Must be at least 6 characters.'
        );
      }
    }

    if (validationErrors.size === 0) {
      trackClick(`talent.${isSignUp ? 'sign_up' : 'sign_in'}`, {
        Intent: intent,
      });
      signXForm.current?.submit();
      setIsSubmitting(true);
    } else {
      setErrors(validationErrors);
    }
  };

  const handleTextFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { target } = e;
    const fieldName = target.dataset.fieldname;

    if (fieldName === 'email') {
      setEmail(target.value);
      setErrors((prevErrors) => prevErrors.delete(fieldName));
    } else if (fieldName === 'password') {
      setPassword(target.value);
      setErrors((prevErrors) => prevErrors.delete(fieldName));
    }
  };

  const handleSubmittableKeyPress = (
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === 'Enter') {
      validateAndSubmit();
    }
  };

  let actionSignUp = 'Create a free Incredible Health account or sign in';
  const actionSignIn = 'Login';

  let title: string | undefined;
  let valueProposition: string | undefined;
  switch (intent) {
    case 'advice':
      title = 'Real Advice for Nurses';
      valueProposition =
        "get advice from Incredible Health's nurse " +
        'community, or share your own expertise';
      actionSignUp = 'Create a free account or sign in';
      break;

    case 'education':
      title = 'Continuing Education';
      valueProposition =
        'get access to 100% free continuing education & much more';
      break;

    case 'mental_health':
      title = 'Nurse Mental Health';
      valueProposition =
        'get access to all of our 100% free tools &' +
        ' services exclusively for nurses';
      actionSignUp = 'Create an account or sign in';
      break;

    case 'network':
      title = 'Nurse Community';
      valueProposition = 'get access to our community of nurses & much more';
      break;

    case 'resume':
      title = 'Resume Builder';
      valueProposition = 'get access to our resume builder & much more';
      break;

    case 'salary': {
      title = 'Nurse salary estimator';
      valueProposition = 'get custom salary estimates & much more';
      break;
    }

    case 'interview_practice':
      title = 'Get Interview-Ready with Gale — Your AI Career Partner';
      valueProposition =
        'Free, private AI interview practice designed for nurses';
      break;

    default:
      console.error(`Unknown intent: ${intent}`);
  }

  const action = isSignUp ? actionSignUp : actionSignIn;
  const subtitle = `${action} to ${valueProposition}`;

  let altPrompt: string;
  let altPromptAction: string;
  let altPromptURL: string;
  if (isSignUp) {
    altPrompt = 'Already have an account?';
    altPromptAction = 'Sign In';
    altPromptURL = `/users/sign_in/${intent}`;
  } else {
    altPrompt = "Don't have an Incredible Health account?";
    altPromptAction = 'Sign Up Here';
    altPromptURL = `/users/sign_up/${intent}`;
  }

  const baseActionURL = `/users/sign_${isSignUp ? 'up' : 'in'}/${intent}`;
  const actionURL = baseActionURL;

  let extraHiddenInputs = null;
  if (isSignUp) {
    const query = qs.parse(window.location.search, {
      ignoreQueryPrefix: true,
    });
    const gclid = getGoogleClickID(query);

    extraHiddenInputs = (
      <>
        <input
          name="user[talent_profile_attributes][signup_intent]"
          type="hidden"
          value={intent}
        />
        {hiddenInputIfValue(
          'user[talent_profile_attributes][source]',
          query.source
        )}
        {hiddenInputIfValue(
          'user[talent_profile_attributes][google_click_id]',
          gclid
        )}
        {sharedConstants.PROFILE_TRACKING_PARAM_NAMES.map((utmParamName) =>
          hiddenInputIfValue(
            `user[talent_profile_attributes][${utmParamName}]`,
            query[utmParamName]
          )
        )}
        {profileAttributes &&
          Object.keys(profileAttributes).length > 0 &&
          Object.keys(profileAttributes).map((key) =>
            hiddenInputIfValue(
              `user[talent_profile_attributes][${key}]`,
              profileAttributes[key]
            )
          )}
      </>
    );
  }

  const termsOfService = (
    <FooterWrapper>
      By signing up you agree to Incredible Health&apos;s{' '}
      <a
        href="https://www.incrediblehealth.com/terms.html"
        onClick={genExternalLinkHandler(true)}
      >
        Terms of Service
      </a>{' '}
      and{' '}
      <a
        href="https://www.incrediblehealth.com/privacy.html"
        onClick={genExternalLinkHandler(true)}
      >
        Privacy Policy
      </a>
    </FooterWrapper>
  );
  const continueButton = (
    <Button
      className="submit"
      onClick={validateAndSubmit}
      disabled={isSubmitting || !password || !email}
      style={{
        marginTop: '15px',
        padding: '10px 24px',
      }}
    >
      Continue
    </Button>
  );
  return (
    <>
      <div>
        <h1
          className={(() => {
            if (!isSignUp) return 'title';
            return null;
          })()}
        >
          {title}
        </h1>
        <Subtitle>{subtitle}</Subtitle>
        <form action={actionURL} id="signXForm" method="POST" ref={signXForm}>
          <InputWrapper>
            <Input
              id="email"
              label="Email address"
              name="user[email]"
              data-fieldname="email"
              value={email}
              onChange={handleTextFieldChange}
              error={Boolean(errors.get('email', false))}
              caption={errors.get('email', '')}
            />
          </InputWrapper>

          <InputWrapper>
            <Input
              id="password"
              label="Password"
              name="user[password]"
              value={password}
              data-fieldname="password"
              onChange={handleTextFieldChange}
              onKeyPress={handleSubmittableKeyPress}
              type="password"
              error={Boolean(errors.get('password', false))}
              caption={errors.get('password', '')}
            />
          </InputWrapper>

          {!isSignUp && (
            <SignInOptionsWrapper>
              <Checkbox
                name="user[remember_me]"
                value={rememberMe.toString()}
                checked={rememberMe}
                labelSize="small"
                labelWeight="normal"
                onChange={() => {
                  setRememberMe((prev) => !prev);
                }}
              >
                Keep me signed in
              </Checkbox>

              <div>
                <a href="/users/password/new">Forgot Password?</a>
              </div>
            </SignInOptionsWrapper>
          )}

          <input
            name="authenticity_token"
            type="hidden"
            value={authenticityToken()}
          />
          {extraHiddenInputs}

          {continueButton}
        </form>
      </div>
      <AltPrompt>
        {altPrompt}
        <br />
        <a href={altPromptURL}>{altPromptAction}</a>
      </AltPrompt>
      {termsOfService}
    </>
  );
}

export default SignXForm;
